'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import { useEffect, useState } from 'react'

import { useRouter } from '@/i18n/routing'
import { onSessionEnd } from '@/shared/api/client'

import {
  getNotificationSettings,
  getPushSettings
} from '../api/notificationsApi'
import { notificationHref } from '../lib/notificationHref'
import {
  dropBrowserPushSubscription,
  isWebPushSupported
} from '../lib/pushSubscription'
import { readSessionUserId } from '../lib/sessionUser'
import { syncThisBrowser, withPushLock } from '../lib/webPush'
import { notificationsQueryKeys } from '../notifications.constants'
import { useNotificationsUserId } from './useNotificationsUserId'

/**
 * Query parameter an older push worker (still cached in some browsers) put
 * the clicked notification's id into. It is only stripped from the address
 * bar now: a push click never marks the Centre record read.
 */
export const NOTIFICATION_READ_PARAM = 'notification_read'

/** Messages exchanged with `public/push-sw.js`. */
const CLICK_MESSAGE = 'arena:notification-click'
const CLICK_ACK = 'arena:notification-click-ack'
const SUBSCRIPTION_CHANGE_MESSAGE = 'arena:push-subscription-change'

interface WorkerMessage {
  type?: unknown
  url?: unknown
}

/**
 * Everything about notifications that follows the session rather than a
 * screen. Mounted once, in AuthProvider:
 *
 * - the cache of any other account is dropped when the account changes —
 *   login, JoinPage, OAuth, logout, expiry, a switch in another tab;
 * - a browser without a session, or whose session died on a request, is
 *   unsubscribed from Web Push;
 * - a signed-in browser's subscription is kept in line with the account
 *   (see `syncThisBrowser`);
 * - a push clicked in the system tray opens its link; the Centre record it
 *   mirrors stays unread until the fan opens it in the Centre (Web Push is
 *   a separate channel).
 */
export function useNotificationsSession() {
  const queryClient = useQueryClient()
  const userId = useNotificationsUserId()
  const language = useLocale()
  const router = useRouter()
  const [syncRound, setSyncRound] = useState(0)

  useEffect(() => {
    queryClient.removeQueries({
      predicate: query =>
        query.queryKey[0] === notificationsQueryKeys.all[0] &&
        query.queryKey[1] !== userId
    })
  }, [queryClient, userId])

  // A session that dies on a request (revoked or expired refresh token) must
  // not leave this browser showing the account's pushes to the next person.
  // The server cannot be told without a token; the push service answers 410
  // for the dropped endpoint and the server deletes it then.
  useEffect(
    () => onSessionEnd(() => withPushLock(dropBrowserPushSubscription)),
    []
  )

  useEffect(() => {
    // The hook's value is still empty while hydrating; the cookies are not.
    if (userId || readSessionUserId() || !isWebPushSupported()) {
      return
    }
    void withPushLock(dropBrowserPushSubscription)
  }, [userId])

  useEffect(() => {
    if (
      !userId ||
      !isWebPushSupported() ||
      Notification.permission !== 'granted'
    ) {
      return
    }

    let cancelled = false

    const pushKey = notificationsQueryKeys.push(userId)

    const pushEnabled = async () => {
      const { settings } = await queryClient.fetchQuery({
        queryKey: notificationsQueryKeys.settings(userId),
        queryFn: getNotificationSettings
      })
      return (settings ?? []).some(
        setting => setting.channel === 'push' && setting.enabled
      )
    }

    // The list of registered browsers is read under the lock: read before a
    // registration in another tab finishes, it would lack the new endpoint
    // and the sync would drop that subscription as foreign.
    const sync = () =>
      withPushLock(async () => {
        if (cancelled || readSessionUserId() !== userId) {
          return false
        }
        const push = await queryClient.fetchQuery({
          queryKey: pushKey,
          queryFn: getPushSettings,
          staleTime: 0
        })
        if (cancelled) {
          return false
        }
        return syncThisBrowser({ userId, language, push, pushEnabled })
      }).then(changed => {
        if (changed && !cancelled) {
          void queryClient.invalidateQueries({ queryKey: pushKey })
        }
      })

    sync().catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [queryClient, userId, language, syncRound])

  useEffect(() => {
    if (!isWebPushSupported()) {
      return
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data as WorkerMessage | null

      if (data?.type === SUBSCRIPTION_CHANGE_MESSAGE) {
        setSyncRound(round => round + 1)
        return
      }
      if (data?.type !== CLICK_MESSAGE) {
        return
      }

      // Tells the worker this tab took over; otherwise it navigates itself.
      event.ports?.[0]?.postMessage({ type: CLICK_ACK })

      const href = notificationHref(
        typeof data.url === 'string' ? data.url : undefined
      )
      if (href) {
        router.push(href)
      }
    }

    const container = navigator.serviceWorker
    container.addEventListener('message', onMessage)

    return () => {
      container.removeEventListener('message', onMessage)
    }
  }, [router])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(NOTIFICATION_READ_PARAM)) {
      return
    }

    url.searchParams.delete(NOTIFICATION_READ_PARAM)
    window.history.replaceState(
      null,
      '',
      `${url.pathname}${url.search}${url.hash}`
    )
  }, [])
}
